import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import identity from 'lodash/fp/identity';
import { useSnackbar } from 'notistack';
import { memo, useRef } from 'react';
import { useIntl } from 'react-intl';
// import { useSelector } from 'react-redux';
import { AuthException, BadRequestException, exceptionOf, ForbiddenException, Token, UnknownException } from './models';
import { forbidden, login, useNavigate, useQuery } from './routes';
import { useResponseInterceptor } from './services/http-hook';
import { fromAuth, useAppDispatch } from './store';
import { useConfig } from './config';
import { isInSystemMaintenance } from './models/config';

const isRequestCanceled = (error: AxiosError | undefined) => (
  axios.isCancel(error)
  || error?.code === 'ERR_CANCELED'
  || error?.name === 'CanceledError'
);

const ErrorHandler = () => {
  const { replace, replaceQuery } = useNavigate();
  const { queryDictionary } = useQuery();
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const { config } = useConfig();
  const dispatch = useAppDispatch();
  const refreshTokenPromiseRef = useRef<Promise<any>>();

  const handleUnauthorized = async (error: AxiosError) => {
    const originalConfig = error.config;
    const storedToken = localStorage.getItem('k1.login') ?? 'null';
    const { refreshJwt } = JSON.parse(storedToken) as Token;
    if (refreshJwt && originalConfig) {
      const refreshResult = await handleRefreshToken(error, originalConfig);
      return refreshResult;
    }
    return error;
  };

  const handleRefreshToken = async (
    error: AxiosError,
    originalConfig: InternalAxiosRequestConfig,
  ) => {
    try {
      if ((originalConfig as any).authRefreshAttempted) {
        replace(login.path, replaceQuery(queryDictionary()));
        return error;
      }
      (originalConfig as any).authRefreshAttempted = true;
      const isOriginRefreshRequest = !refreshTokenPromiseRef.current;
      if (isOriginRefreshRequest) {
        refreshTokenPromiseRef.current = dispatch(fromAuth.doRefreshToken());
      }
      const response = await refreshTokenPromiseRef.current;
      if (fromAuth.doRefreshToken.rejected.match(response)
        || !fromAuth.doRefreshToken.fulfilled.match(response)) {
        replace(login.path, replaceQuery(queryDictionary()));
        return error;
      }
      const { token } = response.payload as { token: Pick<Token, 'jwt' | 'refreshJwt'> };
      if (!token?.jwt) {
        replace(login.path, replaceQuery(queryDictionary()));
        return error;
      }
      if (isOriginRefreshRequest) {
        const resumeResponse = await dispatch(fromAuth.doResume(token));
        if (fromAuth.doResume.rejected.match(resumeResponse)) {
          replace(login.path, replaceQuery(queryDictionary()));
          return error;
        }
      }
      const method = originalConfig.method?.toUpperCase();
      if (method !== 'GET' && method !== 'HEAD') {
        return error;
      }
      originalConfig.headers.Authorization = `Bearer ${token.jwt}`;
    } catch (ex) {
      replace(login.path, replaceQuery(queryDictionary()));
      return error;
    } finally {
      if (refreshTokenPromiseRef.current) {
        refreshTokenPromiseRef.current = undefined;
      }
    }
    const requestAfterRefresh = await axios(originalConfig);
    return requestAfterRefresh;
  };

  useResponseInterceptor(identity, async (error, appConfig) => {
    if (isRequestCanceled(error)) {
      throw error;
    }

    const exception = exceptionOf(error);
    if (isInSystemMaintenance(appConfig)) {
      enqueueSnackbar(
        intl.formatMessage({ id: 'error.system_maintenance_mode' }),
        { variant: 'error' },
      );
    } else if (exception.isInstanceOf(AuthException)) {
      const result = await handleUnauthorized(error);
      enqueueSnackbar(
        intl.formatMessage({ id: exception.message }),
        { variant: 'error' },
      );
      return result;
    }
    if (exception.isInstanceOf(ForbiddenException)) {
      replace(forbidden.path, replaceQuery(queryDictionary()));
      return error;
    }
    if (error.config?.errorPresentation === 'caller') {
      return error;
    }
    if (exception.isInstanceOf(UnknownException)) {
      enqueueSnackbar(
        intl.formatMessage({ id: exception.message }),
        { variant: 'error' },
      );
    }
    if (exception.isInstanceOf(BadRequestException)
      && !exception.message.startsWith('coupon')) {
      enqueueSnackbar(
        intl.formatMessage({ id: exception.message }),
        { variant: 'error' },
      );
    }
    return error;
  }, config);

  return null;
};

export default memo(ErrorHandler);
